import { gql, QueryResult } from '@apollo/client'
import * as React from 'react'
import styled, { css } from 'styled-components'
import addDefaultSrc from '../../../../common/src/addDefaultSrc'
import { clock } from '../../../../common/src/clock'
import * as Icon from '../../../../common/src/components/Icon'
import Indicator from '../../../../common/src/components/Indicator'
import link, { Link } from '../../../../common/src/components/Link'
import { Spacer, BodyText, WithScrollIntoViewIfNeeded, TextTag, H3 } from '../../../../common/src/components/core'
import expandable, { ExpandableProps } from '../../../../common/src/components/expandable'
import { Colors } from '../../../../common/src/constants'
import { TestIds } from '../../../../common/src/data/testids'
import { dateMDYFmt } from '../../../../common/src/date'
import { formatDuration } from '../../../../common/src/format'
import {
  ThreadBotStatus,
  FetchTasksPageQuery,
  FetchUserContextQuery,
  FetchArchivedTaskThreadsQuery,
  FetchArchivedTaskThreadsQueryVariables,
  FetchInboxQuery,
} from '../../../../common/src/types.gen'
import { Fragments } from '../../graphql/fragments'
import { PollingQuery } from '../components/PollingQuery'
import { ContactText } from '../components/contact'
import { ScrollPane } from '../components/containers'
import { TaskText } from '../components/task'
import { getTaskPath, getThreadPath } from '../route'

interface InboxSidebarProps {
  threads?: FetchInboxQuery['self']['unarchivedThreads']
  selectedThreadId?: number
}

interface InboxSidebarState {
  timeNow: string
  showAll: boolean
}

export default class InboxSidebar extends React.PureComponent<InboxSidebarProps, InboxSidebarState> {
  constructor(props: InboxSidebarProps) {
    super(props)
    this.state = createInitialState()
  }

  componentDidMount() {
    clock.addListener(this.onClockBound)
  }

  componentWillUnmount() {
    clock.removeListener(this.onClockBound)
  }

  render() {
    const { showAll } = this.state
    const { listElem, hiddenItemsCount } = this.renderListElem()

    return (
      <Sidebar data-testid={TestIds.inboxSidebar}>
        <ScrollPane>
          <Spacer height={16} />
          {listElem}
          {(hiddenItemsCount > 0 || showAll) && (
            <div>
              <Spacer height={8} />
              <ShowAllToggle onClick={this.toggleShowAllBound}>{this.showAllLabel(hiddenItemsCount)}</ShowAllToggle>
              <Spacer height={8} />
            </div>
          )}
        </ScrollPane>
      </Sidebar>
    )
  }

  private onClockBound = (timeNow: string) => this.setState({ timeNow })

  private showAllLabel = (hiddenTasksCount: number) => {
    const { showAll } = this.state
    if (showAll) {
      return 'collapse'
    } else {
      return `show ${hiddenTasksCount} more threads`
    }
  }

  private toggleShowAllBound = () => {
    const toggled = !this.state.showAll
    this.setState({ showAll: toggled })
  }

  private renderListElem() {
    const { timeNow, showAll } = this.state
    const { threads, selectedThreadId } = this.props

    let listElem = null
    let hiddenItemsCount = 0
    if (threads != null) {
      const unarchivedThreads = threads.filter(t => !t.isArchived)
      const tasksToShow = showAll ? unarchivedThreads.length : 35
      hiddenItemsCount = unarchivedThreads.length - tasksToShow
      listElem = (
        <SidebarThreads>
          {threads.length === 0 && <BodyText>All done! All threads archived. 🎉</BodyText>}
          {unarchivedThreads.slice(0, showAll ? unarchivedThreads.length : 35).map(t => (
            <ThreadItem
              key={'thread-' + t.id}
              timeNow={timeNow}
              thread={t}
              selected={selectedThreadId === t.id}
              shouldScrollToView
            />
          ))}
        </SidebarThreads>
      )
    }
    return { listElem, hiddenItemsCount }
  }
}

class ThreadItem extends React.PureComponent<{
  timeNow: string
  thread: FetchInboxQuery['self']['unarchivedThreads'][0]
  selected: boolean
  shouldScrollToView?: boolean
}> {
  private rowElem?: WithScrollIntoViewIfNeeded<HTMLElement>

  UNSAFE_componentWillReceiveProps(np: ThreadItem['props']) {
    if (np.selected && !this.props.selected) {
      this.scrollThreadToView()
    }
  }

  render() {
    const { timeNow, thread, selected } = this.props

    const selColor = selected ? 'white' : thread.isArchived ? 'steel' : 'midway'
    let contactTag = null
    const contactPrefix = <ContactPrefix>{Icon.convoTypeToIcon(thread.conversationType)}</ContactPrefix>
    if (thread.botStatus !== ThreadBotStatus.ACTIVE) {
      const tagText = thread.botStatus === ThreadBotStatus.STOPPED ? 'STOP' : 'BLCK'
      contactTag = <ThreadTextTag color={selColor}>{tagText}</ThreadTextTag>
    }
    const isRead = thread.lastReadMessageId >= thread.lastInboundMessageId
    const isRepliedTo = !thread.lastMessageInbound

    const { botThread } = thread
    const tSS = botThread && botThread.showCountdown ? botThread.sendTime : null

    return (
      <SidebarRow key={thread.id} ref={this.rowRefBound}>
        <SidebarThreadDate threadTime={getThreadTime(thread)} sendTime={tSS} timeNow={timeNow} />
        <ThreadLink linkClassName={'thread-link'} color={selColor} to={getThreadPath(thread.id)} noTab>
          {contactPrefix}
          <ContactText contact={thread.contactThem} />
          {contactTag}
        </ThreadLink>
        <Spacer grow />
        {!isRead && <Indicator color="sky" />}
        {isRead && !isRepliedTo && <Indicator color="silver" />}
        <Spacer width={16} />
      </SidebarRow>
    )
  }

  private rowRefBound = (e: HTMLDivElement) => {
    this.rowElem = e
    if (this.props.selected) {
      this.scrollThreadToView()
    }
  }

  private scrollThreadToView() {
    if (!this.props.shouldScrollToView) {
      return
    }

    if (this.rowElem != null) {
      console.log('ThreadItem: scrolling into view')
      if (this.rowElem.scrollIntoViewIfNeeded) {
        this.rowElem.scrollIntoViewIfNeeded(false)
      } else {
        this.rowElem.scrollIntoView()
      }
    }
  }
}

function SidebarThreadDate({
  threadTime,
  sendTime,
  timeNow,
}: {
  threadTime?: string | null
  sendTime?: string | null
  timeNow?: string | null
}) {
  let elem = null
  if (sendTime != null && timeNow != null) {
    const millisUntil = new Date(sendTime).getTime() - new Date(timeNow).getTime()
    const str = millisUntil < 0 ? 'NOW' : formatDuration(millisUntil)
    elem = <BodyText color="sky">{str}</BodyText>
  } else if (threadTime != null) {
    elem = <BodyText color="steel">{dateMDYFmt(threadTime)}</BodyText>
  }
  return <SidebarThreadDateRoot>{elem}</SidebarThreadDateRoot>
}

function getThreadTime(thread: { sortTime: string }): string {
  return thread.sortTime
}

const Sidebar = styled.div`
  border-right: 1px solid #2f2c30;
  width: 320px;
  min-width: 320px;
`

const ShowAllToggle = styled(BodyText)`
  cursor: pointer;
  text-align: center;
`

const SidebarThreads = styled.div`
  padding: 0 0 12px 24px;
`

const SidebarRow = styled(BodyText)`
  display: flex;
  padding: 2px 0;
  min-height: 28px;
  white-space: nowrap;
  &:hover {
    filter: brightness(120%);
  }
  &:active {
    filter: brightness(140%);
  }
`

const ContactPrefix = styled.span`
  width: 20px;
  display: inline-block;
`

const SidebarThreadDateRoot = styled.div`
  padding-right: 24px;
  text-align: right;
  width: 48px;
  font-feature-settings: 'tnum' on; /* tabular numbers don't jump when animated */
`

const ThreadTextTag = styled(TextTag)`
  margin-top: -3px;
  margin-left: 6px;
`

interface TasksSidebarProps {
  tasks: FetchTasksPageQuery['allFirmTasks']
  selectedTaskId?: number
  selectedThreadId?: number
  user: FetchUserContextQuery['self']
}

interface TasksSidebarState {
  timeNow: string
  showAll: boolean
}

export class TasksSidebar extends React.PureComponent<TasksSidebarProps, TasksSidebarState> {
  constructor(props: TasksSidebarProps) {
    super(props)
    this.state = createInitialState()
  }

  componentDidMount() {
    clock.addListener(this.onClockBound)
  }

  componentWillUnmount() {
    clock.removeListener(this.onClockBound)
  }

  render() {
    const { showAll } = this.state
    const { listElem, hiddenItemsCount } = this.renderListElem()

    return (
      <Sidebar>
        <ScrollPane>
          {listElem}
          {(hiddenItemsCount > 0 || showAll) && (
            <div>
              <Spacer height={16} />
              <ShowAllToggle onClick={this.toggleShowAllBound}>{this.showAllLabel(hiddenItemsCount)}</ShowAllToggle>
              <Spacer height={8} />
            </div>
          )}
        </ScrollPane>
      </Sidebar>
    )
  }

  private onClockBound = (timeNow: string) => this.setState({ timeNow })

  private showAllLabel = (hiddenTasksCount: number) => {
    const { showAll } = this.state
    if (showAll) {
      return 'collapse'
    } else {
      return `show ${hiddenTasksCount} more tasks`
    }
  }

  private toggleShowAllBound = () => {
    const toggled = !this.state.showAll
    this.setState({ showAll: toggled })
  }

  private renderListElem() {
    const { timeNow, showAll } = this.state
    const { tasks, selectedTaskId, selectedThreadId } = this.props

    let listElem = null
    let hiddenItemsCount = 0
    const selTaskIndex = tasks.findIndex(t => t.id === selectedTaskId)
    const numTasksToShow = showAll ? tasks.length : Math.max(20, selTaskIndex + 1)
    hiddenItemsCount = tasks.length - numTasksToShow
    const tasksToShow = tasks.slice(0, numTasksToShow)
    listElem = tasksToShow.map(task => (
      <LeasingTaskItem
        key={'task-' + task.id}
        timeNow={timeNow}
        leasingTask={task}
        selected={selectedTaskId === task.id}
        selectedThreadId={selectedThreadId}
      />
    ))
    return { listElem, hiddenItemsCount }
  }
}

interface LeasingTaskItemProps {
  timeNow: string
  leasingTask: FetchTasksPageQuery['allFirmTasks'][0]
  selected: boolean
  selectedThreadId?: number
}

type LeasingTaskItemImplProps = LeasingTaskItemProps &
  ExpandableProps & { data?: { task: FetchArchivedTaskThreadsQuery['task'] } }

class LeasingTaskItemImpl extends React.PureComponent<LeasingTaskItemImplProps> {
  private itemElem?: WithScrollIntoViewIfNeeded<HTMLDivElement>

  UNSAFE_componentWillReceiveProps(np: LeasingTaskItemImplProps) {
    if (np.selected && !this.props.selected) {
      this.scrollTaskToView()
    }
  }

  render() {
    const { leasingTask, selected, isExpanded, data } = this.props
    const { streetViewAddress, threads, isArchived } = leasingTask

    const loadedArchivedThreads = data && data.task && data.task.archivedThreads
    const allThreads = threads.concat(loadedArchivedThreads || [])

    let threadList = null
    const threadsActive = sortThreads(allThreads.filter(t => !t.isArchived))
    const threadsArchived = sortThreads(allThreads.filter(t => t.isArchived))
    const maxArchived = threadsArchived.length > 200 ? 100 : 200

    threadList = (
      <BodyText>
        {threadsActive.map(thread => this.renderThreadItem(thread))}
        {isExpanded && threadsArchived.slice(0, maxArchived).map(thread => this.renderThreadItem(thread))}
        {isExpanded && threadsArchived.length > maxArchived && (
          <SidebarRow>
            <SidebarThreadDate />+{threadsArchived.length - maxArchived} not shown
          </SidebarRow>
        )}
        {isExpanded && loadedArchivedThreads && loadedArchivedThreads.length === 0 && (
          <SidebarRow>
            <SidebarThreadDate />
            No archived threads
          </SidebarRow>
        )}

        <ThreadListExpandToggle isExpanded={isExpanded} onClick={this.props.toggleExpand} />
      </BodyText>
    )

    return (
      <SidebarItem ref={this.itemRefBound}>
        <TaskHeader>
          <PropertyThumbnail address={streetViewAddress} />
          <TaskHeaderLink selected={selected} isArchived={isArchived} to={getTaskPath(leasingTask.id)} noTab>
            <TaskText task={leasingTask} />
          </TaskHeaderLink>
        </TaskHeader>
        {threadList}
      </SidebarItem>
    )
  }

  private itemRefBound = (e: HTMLDivElement) => {
    this.itemElem = e
    if (this.props.selected) {
      this.scrollTaskToView()
    }
  }

  private scrollTaskToView() {
    if (this.itemElem != null) {
      console.log('LeasingTaskItem scrolling into view')
      if (this.itemElem.scrollIntoViewIfNeeded) {
        this.itemElem.scrollIntoViewIfNeeded(false)
      } else {
        this.itemElem.scrollIntoView()
      }
    }
  }

  private renderThreadItem(thread: FetchInboxQuery['self']['unarchivedThreads'][0]) {
    const { timeNow, selectedThreadId } = this.props
    return (
      <ThreadItem
        key={'thread-' + thread.id}
        timeNow={timeNow}
        thread={thread}
        selected={thread.id === selectedThreadId}
      />
    )
  }
}

export const fetchArchivedThreadsQuery = gql`
  query FetchArchivedTaskThreads($taskId: Int!) {
    task(taskId: $taskId) {
      id
      archivedThreads {
        ...Thread
      }
    }
  }
  ${Fragments.thread}
  ${Fragments.contact}
`

function LeasingTaskItemLoader(props: LeasingTaskItemProps & ExpandableProps) {
  if (!props.isExpanded) {
    return <LeasingTaskItemImpl {...props} />
  }
  const taskId = props.leasingTask.id
  console.log('inbox loading archived threads for task %s', taskId)
  return (
    <PollingQuery query={fetchArchivedThreadsQuery} variables={{ taskId }}>
      {(res: QueryResult<FetchArchivedTaskThreadsQuery, FetchArchivedTaskThreadsQueryVariables>) => (
        <LeasingTaskItemImpl {...props} {...res} />
      )}
    </PollingQuery>
  )
}

const LeasingTaskItem = expandable(LeasingTaskItemLoader)

// Sorts IN PLACE by sortKey, returns `threads`
function sortThreads(threads: FetchInboxQuery['self']['unarchivedThreads']) {
  threads.sort((a, b) => b.sortTime.localeCompare(a.sortTime))
  return threads
}

function ThreadListExpandToggle({
  isExpanded,
  onClick,
}: {
  isExpanded: boolean
  onClick: (e: React.MouseEvent<HTMLElement>) => void
}) {
  return (
    <SidebarRow>
      <SidebarThreadDate />
      <Link color="silver" onClick={onClick}>
        {isExpanded ? 'Hide archived' : 'Show archived'}
      </Link>
    </SidebarRow>
  )
}

const SidebarItem = styled.div`
  border-bottom: 1px solid #2f2c30;
  padding: 24px 0 8px 24px;
`

const TaskHeader = styled(SidebarRow)`
  padding: 0;
`

const TaskHeaderLink = link(styled(H3)`
  ${(props: { isArchived?: boolean; selected?: boolean }) => css`
    border-bottom: ${props.isArchived ? '' : '1px solid ' + Colors.charcoal};
    color: ${props.selected ? Colors.sky : props.isArchived ? Colors.midway : Colors.white};
    cursor: pointer;
    flex: 1;
    margin-bottom: ${props.isArchived ? '0' : '16px'};
    padding-bottom: 16px;
  `};
`)

function PropertyThumbnail({ address }: { address?: string | null }) {
  const src = 'https://dynasty-streetview.s3-us-west-2.amazonaws.com/' + address + ' SMALL'
  return (
    <PropertyThumbnailRoot>
      <img
        src={src}
        onError={addDefaultSrc(
          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Crect fill='%23333' width='48' height='48'/%3E%3C/svg%3E"
        )}
      />
    </PropertyThumbnailRoot>
  )
}

const PropertyThumbnailRoot = styled.div`
  width: 48px;
  height: 48px;
  margin-right: 24px;
  overflow: hidden;
`

function createInitialState() {
  return {
    timeNow: clock.now(),
    showAll: false,
  }
}

const ThreadLink = link(styled(BodyText)`
  ${() => css`
    cursor: pointer;
    padding-bottom: 6px; /* larger click target */
    &:focus,
    &:hover {
      color: ${Colors.silver};
      transition: all 0.05s linear 0s;
    }
    &:active {
      color: ${Colors.snow};
      transition: all 0.02s linear 0s;
    }
  `};
`)
